import { InfoWithLabel } from "@/core/components/info-with-label";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetActivityReferral } from "@/activity-feed/query";
import { formatDate } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { getCustomerNameOrPhone } from "@/customer/utils";
import { ISpiff, ReferralTrigger } from "@/spiffs/spiffs.d";

interface ClosedReferralTriggerDetailsProps {
  spiff: ISpiff;
}

export const ClosedReferralTriggerDetails = ({
  spiff,
}: ClosedReferralTriggerDetailsProps) => {
  const referralTrigger = spiff.trigger as ReferralTrigger;
  const { data: referral, isLoading } = useGetActivityReferral(
    referralTrigger.id,
  );

  if (isLoading) {
    return (
      <Stack gap="sm" fullWidth>
        <InfoWithLabel label="Customer">
          <Skeleton className="h-5 w-48" />
        </InfoWithLabel>
        <Stack direction="row" gap="none" fullWidth>
          <InfoWithLabel label="Service">
            <Skeleton className="h-5 w-32" />
          </InfoWithLabel>
          <InfoWithLabel label="Job Completed">
            <Skeleton className="h-5 w-32" />
          </InfoWithLabel>
        </Stack>
        <Stack direction="row" gap="none" fullWidth>
          <InfoWithLabel label="Deal Size">
            <Skeleton className="h-5 w-24" />
          </InfoWithLabel>
          <InfoWithLabel label="Attributed">
            <Skeleton className="h-5 w-40" />
          </InfoWithLabel>
        </Stack>
      </Stack>
    );
  }

  if (!referral) {
    return (
      <InfoWithLabel label="Referral">
        <Typography variant="bodyW1" textColor="secondary">
          Referral information unavailable
        </Typography>
      </InfoWithLabel>
    );
  }

  return (
    <Stack gap="sm" fullWidth>
      <InfoWithLabel label="Customer">
        {getCustomerNameOrPhone(referral.potential_customer)}
      </InfoWithLabel>
      <Stack direction="row" gap="none" fullWidth>
        <InfoWithLabel label="Service">{referral.service_needed}</InfoWithLabel>
        <InfoWithLabel label="Job Completed">
          {formatDate(referral.appointment_date)}
        </InfoWithLabel>
      </Stack>
      <Stack direction="row" gap="none" fullWidth>
        <InfoWithLabel label="Deal Size">
          {formatMoney(referral.deal_size)}
        </InfoWithLabel>
        <InfoWithLabel label="Attributed">
          {getCustomerNameOrPhone(referral.advocate)}
        </InfoWithLabel>
      </Stack>
    </Stack>
  );
};
